import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  ShieldCheck, PenTool, CheckCircle2, Download, HeartPulse, ChevronDown, PartyPopper, Loader2, Home,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ALLERGY_OPTIONS = [
  { id: 'peanuts', label: 'Peanuts' }, { id: 'tree_nuts', label: 'Tree Nuts' },
  { id: 'dairy', label: 'Dairy' }, { id: 'gluten', label: 'Gluten' },
  { id: 'eggs', label: 'Eggs' }, { id: 'soy', label: 'Soy' },
  { id: 'shellfish', label: 'Shellfish' },
];
const MEDICAL_OPTIONS = [
  { id: 'asthma', label: 'Asthma' }, { id: 'epilepsy', label: 'Epilepsy' },
  { id: 'diabetes', label: 'Diabetes' }, { id: 'adhd', label: 'ADHD' },
  { id: 'autism', label: 'Autism' },
];

const errMsg = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(' ');
  return fb || 'Something went wrong';
};

const BrandBar = () => (
  <div className="w-full bg-[#1E1E24] py-3 px-5 flex items-center gap-3" data-testid="waiver-brandbar">
    <a href="/" className="flex items-center gap-2 group" data-testid="waiver-logo-home" aria-label="Creative Birthdays home">
      <PartyPopper className="w-6 h-6 text-[#FF3B6B] transition-transform group-hover:-rotate-12" />
      <span className="text-white text-lg font-semibold" style={{ fontFamily: 'Fredoka, sans-serif' }}>Creative Birthdays</span>
    </a>
    <a href="/"
      className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
      data-testid="waiver-home-btn">
      <Home className="w-4 h-4" /> Home
    </a>
  </div>
);

export const WaiverForm = () => {
  const [params] = useSearchParams();
  const source = params.get('source') || 'public_web';
  const bookingId = params.get('booking_id') || null;

  const [waiverText, setWaiverText] = useState({ title: '', body: '', version: 1 });
  const [loadingText, setLoadingText] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // response object on success
  const [showMedical, setShowMedical] = useState(false);

  const [form, setForm] = useState({
    guardian_name: '', guardian_email: '', guardian_phone: '',
    child_name: params.get('child_name') || '', child_age: '',
    photo_video_consent: true, waiver_accepted: false, signature_name: '',
    allergies: [], allergy_other: '', medical_conditions: [], medical_other: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
    company_website: '', // honeypot
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, id) => setForm((f) => ({
    ...f, [k]: f[k].includes(id) ? f[k].filter((x) => x !== id) : [...f[k], id],
  }));

  const loadText = useCallback(async () => {
    setLoadingText(true);
    try {
      const { data } = await axios.get(`${API}/api/waiver/text`);
      setWaiverText(data);
    } catch (e) { toast.error(errMsg(e, 'Could not load the waiver')); }
    finally { setLoadingText(false); }
  }, []);
  useEffect(() => { loadText(); }, [loadText]);

  const submit = async () => {
    if (!form.guardian_name.trim()) return toast.error('Please enter your name');
    if (!form.child_name.trim()) return toast.error("Please enter your child's name");
    if (!form.guardian_email.trim() && !form.guardian_phone.trim()) return toast.error('Enter an email or phone number');
    if (!form.waiver_accepted) return toast.error('Please read and accept the waiver');
    if (!form.signature_name.trim()) return toast.error('Please type your full name to sign');

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        child_age: form.child_age ? Number(form.child_age) : null,
        signature_date: new Date().toISOString().slice(0, 10),
        source, booking_id: bookingId,
      };
      const { data } = await axios.post(`${API}/api/waiver`, payload);
      setDone(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { toast.error(errMsg(e, 'Could not submit the waiver')); }
    finally { setSubmitting(false); }
  };

  const resetForNext = () => {
    setDone(null);
    setForm((f) => ({
      ...f, child_name: '', child_age: '', waiver_accepted: false, signature_name: '',
      allergies: [], allergy_other: '', medical_conditions: [], medical_other: '',
      emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
    }));
    setShowMedical(false);
  };

  // ── Success screen ──
  if (done && !done.spam) {
    return (
      <div className="min-h-screen bg-[#FFFDF8]">
        <BrandBar />
        <div className="max-w-lg mx-auto px-5 py-14">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl border border-[#E8E5DF] p-8 text-center shadow-sm" data-testid="waiver-success">
            <div className="w-16 h-16 rounded-full bg-[#06D6A0]/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-9 h-9 text-[#06D6A0]" />
            </div>
            <h1 className="text-2xl font-semibold text-[#1E1E24] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {done.already_valid ? 'Waiver refreshed!' : 'Waiver signed!'}
            </h1>
            <p className="text-[#6B6B7A] text-sm mb-1">
              Thank you — the liability waiver for <strong>{form.child_name}</strong> is on file.
            </p>
            {done.valid_until && (
              <p className="text-[#6B6B7A] text-sm mb-6" data-testid="waiver-valid-until">
                Valid until <strong>{String(done.valid_until).slice(0, 10)}</strong>
                {done.linked ? ' · linked to your booking' : ''}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href={`${API}/api/waiver/${done.form_id}/pdf`} target="_blank" rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#1E1E24] text-white text-sm font-semibold hover:bg-black transition-colors"
                data-testid="waiver-download-pdf">
                <Download className="w-4 h-4" /> Download signed waiver
              </a>
              <Button variant="outline" onClick={resetForNext} className="rounded-full" data-testid="waiver-sign-another">
                Sign for another child
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  const inputCls = 'mt-1 rounded-xl border-[#E8E5DF]';

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <BrandBar />
      <div className="max-w-2xl mx-auto px-5 py-8 sm:py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="w-12 h-12 rounded-2xl bg-[#FF3B6B]/10 flex items-center justify-center mb-3">
            <ShieldCheck className="w-7 h-7 text-[#FF3B6B]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Liability Waiver
          </h1>
          <p className="text-[#6B6B7A] mt-2 text-sm sm:text-base">
            Please review and sign before your party. Takes about a minute — valid for one full year.
          </p>
        </motion.div>

        <div className="bg-white rounded-3xl border border-[#E8E5DF] p-6 sm:p-8 shadow-sm space-y-6" data-testid="waiver-form">
          {/* Guardian */}
          <div>
            <h2 className="text-base font-semibold text-[#1E1E24] mb-3">Parent / Guardian</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-xs text-[#6B6B7A]">Your full name *</Label>
                <Input value={form.guardian_name} onChange={(e) => set('guardian_name', e.target.value)} className={inputCls} data-testid="waiver-guardian-name" placeholder="Jane Smith" />
              </div>
              <div>
                <Label className="text-xs text-[#6B6B7A]">Email</Label>
                <Input type="email" value={form.guardian_email} onChange={(e) => set('guardian_email', e.target.value)} className={inputCls} data-testid="waiver-guardian-email" placeholder="you@email.com" />
              </div>
              <div>
                <Label className="text-xs text-[#6B6B7A]">Phone</Label>
                <Input value={form.guardian_phone} onChange={(e) => set('guardian_phone', e.target.value)} className={inputCls} data-testid="waiver-guardian-phone" placeholder="905-555-1234" />
              </div>
            </div>
            <p className="text-[11px] text-[#9CA3AF] mt-2">Provide at least an email or a phone number so we can match your waiver.</p>
          </div>

          {/* Child */}
          <div className="pt-2 border-t border-[#F0EEE9]">
            <h2 className="text-base font-semibold text-[#1E1E24] mb-3">Child / Participant</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-xs text-[#6B6B7A]">Child's full name *</Label>
                <Input value={form.child_name} onChange={(e) => set('child_name', e.target.value)} className={inputCls} data-testid="waiver-child-name" placeholder="Timmy Smith" />
              </div>
              <div>
                <Label className="text-xs text-[#6B6B7A]">Age</Label>
                <Input type="number" min="0" max="18" value={form.child_age} onChange={(e) => set('child_age', e.target.value)} className={inputCls} data-testid="waiver-child-age" placeholder="7" />
              </div>
            </div>
          </div>

          {/* Optional medical */}
          <div className="pt-2 border-t border-[#F0EEE9]">
            <button type="button" onClick={() => setShowMedical((s) => !s)}
              className="w-full flex items-center gap-2 text-left" data-testid="waiver-toggle-medical">
              <HeartPulse className="w-4 h-4 text-[#FF3B6B]" />
              <span className="text-base font-semibold text-[#1E1E24]">Allergies & medical <span className="text-[#9CA3AF] font-normal">(optional)</span></span>
              <ChevronDown className={`w-4 h-4 ml-auto text-[#6B6B7A] transition-transform ${showMedical ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence initial={false}>
              {showMedical && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden" data-testid="waiver-medical-section">
                  <div className="pt-4 space-y-4">
                    <div>
                      <Label className="text-xs text-[#6B6B7A] mb-2 block">Allergies</Label>
                      <div className="flex flex-wrap gap-2">
                        {ALLERGY_OPTIONS.map((o) => (
                          <button key={o.id} type="button" onClick={() => toggleArr('allergies', o.id)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${form.allergies.includes(o.id) ? 'bg-[#FF3B6B] text-white border-[#FF3B6B]' : 'bg-white text-[#6B6B7A] border-[#E8E5DF]'}`}
                            data-testid={`waiver-allergy-${o.id}`}>{o.label}</button>
                        ))}
                      </div>
                      <Input value={form.allergy_other} onChange={(e) => set('allergy_other', e.target.value)} className={`${inputCls} mt-2`} data-testid="waiver-allergy-other" placeholder="Other allergies…" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#6B6B7A] mb-2 block">Medical conditions</Label>
                      <div className="flex flex-wrap gap-2">
                        {MEDICAL_OPTIONS.map((o) => (
                          <button key={o.id} type="button" onClick={() => toggleArr('medical_conditions', o.id)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${form.medical_conditions.includes(o.id) ? 'bg-[#FF3B6B] text-white border-[#FF3B6B]' : 'bg-white text-[#6B6B7A] border-[#E8E5DF]'}`}
                            data-testid={`waiver-medical-${o.id}`}>{o.label}</button>
                        ))}
                      </div>
                      <Input value={form.medical_other} onChange={(e) => set('medical_other', e.target.value)} className={`${inputCls} mt-2`} data-testid="waiver-medical-other" placeholder="Other conditions…" />
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                      <div><Label className="text-xs text-[#6B6B7A]">Emergency contact</Label>
                        <Input value={form.emergency_contact_name} onChange={(e) => set('emergency_contact_name', e.target.value)} className={inputCls} data-testid="waiver-ec-name" placeholder="Name" /></div>
                      <div><Label className="text-xs text-[#6B6B7A]">Phone</Label>
                        <Input value={form.emergency_contact_phone} onChange={(e) => set('emergency_contact_phone', e.target.value)} className={inputCls} data-testid="waiver-ec-phone" placeholder="Phone" /></div>
                      <div><Label className="text-xs text-[#6B6B7A]">Relationship</Label>
                        <Input value={form.emergency_contact_relationship} onChange={(e) => set('emergency_contact_relationship', e.target.value)} className={inputCls} data-testid="waiver-ec-rel" placeholder="e.g. Father" /></div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Photo consent */}
          <div className="pt-2 border-t border-[#F0EEE9] flex items-center justify-between">
            <div className="pr-4">
              <p className="text-sm font-medium text-[#1E1E24]">Photo & video consent</p>
              <p className="text-xs text-[#9CA3AF]">Allow us to photograph the event for our records and promotion.</p>
            </div>
            <Switch checked={form.photo_video_consent} onCheckedChange={(v) => set('photo_video_consent', v)} data-testid="waiver-photo-consent" />
          </div>

          {/* Waiver text */}
          <div className="pt-2 border-t border-[#F0EEE9]">
            <h2 className="text-base font-semibold text-[#1E1E24] mb-1">{waiverText.title || 'Liability Waiver'}</h2>
            <div className="bg-[#FBFAF7] border border-[#E8E5DF] rounded-2xl p-4 max-h-56 overflow-y-auto text-xs leading-relaxed text-[#4B4B55] whitespace-pre-line" data-testid="waiver-text-body">
              {loadingText ? 'Loading waiver…' : waiverText.body}
            </div>
          </div>

          {/* Accept + sign */}
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={form.waiver_accepted} onCheckedChange={(v) => set('waiver_accepted', !!v)} className="mt-0.5" data-testid="waiver-accept-checkbox" />
              <span className="text-sm text-[#1E1E24]">I have read and agree to the liability waiver above, and I am the parent or legal guardian of the child named above.</span>
            </label>
            <div>
              <Label className="text-xs text-[#6B6B7A] flex items-center gap-1"><PenTool className="w-3 h-3" /> Type your full name to sign *</Label>
              <Input value={form.signature_name} onChange={(e) => set('signature_name', e.target.value)} className={`${inputCls} font-[cursive] text-lg`} data-testid="waiver-signature" placeholder="Jane Smith" />
              <p className="text-[11px] text-[#9CA3AF] mt-1">Signed on {new Date().toLocaleDateString()} · this counts as your legal electronic signature.</p>
            </div>
          </div>

          {/* Honeypot — visually hidden */}
          <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true"
            value={form.company_website} onChange={(e) => set('company_website', e.target.value)}
            style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} data-testid="waiver-honeypot" />

          <Button onClick={submit} disabled={submitting}
            className="w-full rounded-full bg-[#FF3B6B] hover:bg-[#E6325F] text-white py-6 text-base font-semibold" data-testid="waiver-submit">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : 'Sign & Submit Waiver'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WaiverForm;
